import { EventType, FailureContext, generateEventTypeMapDefaults, WorkflowEventEventTypeMap } from '@aiwyn/shared';
import { PaymentMethodValidationError } from '@aiwyn/api';
import { PaymentFlow, PaymentMethodIntent } from '../../platforms/models/payment-method-metadata.models';
import { UserType } from '@aiwyn/auth';

/**
 * Flow 7 — Managing Payment Methods.
 * The user adds a new payment method, updates billing details, marks one as default, or removes one.
 * Card/ACH validation flows through Stripe Elements before attach.
 *
 * Removal is gated by two distinct validations: a domain-level check (active payment plans / autopay
 * / engagement usage) and a platform-level check (Stripe pending payments). Either failing emits the
 * matching `*_VALIDATION_FAILED` event with `errorCategory: 'VALIDATION_BLOCKED'` plus the
 * `isUsedBy*` booleans — the "blocked" case isn't a separate event, it's a value of `errorCategory`.
 *
 * Every emitted event carries `PaymentMethodEventBaseMetadata` (see below). The envelope is owned by
 * the per-container `PaymentMethodTrackService` (signal-driven setters from the host component) and
 * merged into each `trackWorkflow` call at dispatch — per-site call sites only pass event-specific
 * fields.
 */
export enum PaymentMethodWorkflowEvent {
  // Add flow
  PAYMENT_METHOD_ADD_STARTED = 'PAYMENT_METHOD_ADD_STARTED',
  PAYMENT_METHOD_TYPE_SELECTED = 'PAYMENT_METHOD_TYPE_SELECTED',
  PAYMENT_METHOD_VALIDATION_SUBMITTED = 'PAYMENT_METHOD_VALIDATION_SUBMITTED',
  PAYMENT_METHOD_SETUP_CONFIRMED = 'PAYMENT_METHOD_SETUP_CONFIRMED',
  PAYMENT_METHOD_ATTACH_SUCCEEDED = 'PAYMENT_METHOD_ATTACH_SUCCEEDED',
  PAYMENT_METHOD_ATTACH_FAILED = 'PAYMENT_METHOD_ATTACH_FAILED',
  PAYMENT_METHOD_PLATFORM_INIT_FAILED = 'PAYMENT_METHOD_PLATFORM_INIT_FAILED',
  PAYMENT_METHOD_ADD_CANCELLED = 'PAYMENT_METHOD_ADD_CANCELLED',
  PAYMENT_METHOD_ADD_COMPLETE = 'PAYMENT_METHOD_ADD_COMPLETE',

  // Manage existing
  PAYMENT_METHOD_UPDATE_STARTED = 'PAYMENT_METHOD_UPDATE_STARTED',
  PAYMENT_METHOD_UPDATE_CANCELLED = 'PAYMENT_METHOD_UPDATE_CANCELLED',
  PAYMENT_METHOD_UPDATE_SUCCEEDED = 'PAYMENT_METHOD_UPDATE_SUCCEEDED',
  PAYMENT_METHOD_UPDATE_FAILED = 'PAYMENT_METHOD_UPDATE_FAILED',
  PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED = 'PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED',
  PAYMENT_METHOD_SET_DEFAULT_FAILED = 'PAYMENT_METHOD_SET_DEFAULT_FAILED',

  // Remove flow
  PAYMENT_METHOD_REMOVE_STARTED = 'PAYMENT_METHOD_REMOVE_STARTED',
  PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUBMITTED = 'PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUBMITTED',
  PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUCCEEDED = 'PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUCCEEDED',
  PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED = 'PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED',
  PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUBMITTED = 'PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUBMITTED',
  PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUCCEEDED = 'PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUCCEEDED',
  PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED = 'PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED',
  PAYMENT_METHOD_REMOVE_CANCELLED = 'PAYMENT_METHOD_REMOVE_CANCELLED',
  PAYMENT_METHOD_REMOVE_SUCCEEDED = 'PAYMENT_METHOD_REMOVE_SUCCEEDED',
  PAYMENT_METHOD_REMOVE_FAILED = 'PAYMENT_METHOD_REMOVE_FAILED',
}

/**
 * Discriminator for "where in the product was the user when this event fired." One value per
 * physically distinct host of the add/manage flows so dashboards don't collapse meaningfully
 * different journeys into one bucket.
 *
 * Placeholder values — intentionally dead until a fire site is wired:
 *  - `cxp-deliverables-pay-invoice` — reserved for the deliverables-fork
 *    `StripeSelectPaymentMethodComponent`. The fork has no tracking today; the value is in the
 *    union so the surface taxonomy stays complete for when the fork is folded back in or
 *    instrumented (see `payment-method-tracking-phases.md` phase "Out of scope").
 *
 * Live values:
 *  - `payments-sidebar-{invoices, review-and-pay, quick-payment-link}` — via `SelectPaymentMethodComponent`
 *    hosted by `PaymentsSidebarComponent`. Mirrors the `PaymentSurface` slices used by the payment-flow
 *    funnel, resolved via `PaymentsState.resolvePaymentSurface()` so URL classification stays the
 *    single source of truth. `payments-sidebar-quick-payment-link` also absorbs open-portal traffic
 *    (open-portal lives on `/quick-payments`).
 *  - `settings-payment-methods` — direct opener: `SavedPaymentMethodsComponent` (settings "Saved
 *    Payment Methods" Add button + per-row Remove/Update/Set-default actions).
 *  - `settings-payment-plan-modal` — via `SelectPaymentMethodComponent` hosted in the Payment Plan
 *    setup modal (intent=PAYMENT, paymentFlow=PLAN).
 *  - `settings-autopay-dialog` — via `SelectPaymentMethodComponent` hosted in the AutoPay setup/edit
 *    dialog (intent=PAYMENT, paymentFlow=PLAN).
 *  - `engagement-payment-info-step` — via `SelectPaymentMethodComponent` embedded in the engagement
 *    signing flow's payment-info step.
 */
export type PaymentMethodSurface =
  | 'payments-sidebar-invoices'
  | 'payments-sidebar-review-and-pay'
  | 'payments-sidebar-quick-payment-link'
  | 'settings-payment-methods'
  | 'settings-payment-plan-modal'
  | 'settings-autopay-dialog'
  | 'engagement-payment-info-step'
  | 'cxp-deliverables-pay-invoice';

/**
 * Common context attached to every `PaymentMethodWorkflowEvent`. Mirrors the role
 * `PaymentEventBaseMetadata` plays for the payment flow — lets a single Pendo / LogRocket funnel
 * filter by surface, intent, user-type, and first-method status without joining across events.
 *
 *  - `surface` — required everywhere. The host that produced the event.
 *  - `intent` — `SETUP | PAYMENT` for events fired during the add flow; `undefined` for
 *    manage/remove/update events where no add-intent applies.
 *  - `paymentFlow` — `STANDARD | PLAN` only when `intent === PAYMENT`; `undefined` otherwise.
 *  - `userType` — required. Pulled from `AuthStateAdaptorService.getUserTypeSnapshot()`.
 *  - `isFirstPaymentMethod` — `true` if the user has zero saved methods at the moment the
 *    event fires; `undefined` outside the add flow (it's only a meaningful signal at add time).
 *
 * The envelope is held by `PaymentMethodTrackService` as signal-driven fields, populated by the
 * host container's `set*` calls, and merged into per-call metadata at dispatch.
 */
export type PaymentMethodEventBaseMetadata = {
  surface: PaymentMethodSurface;
  intent: PaymentMethodIntent | undefined;
  paymentFlow: PaymentFlow | undefined;
  userType: UserType;
  isFirstPaymentMethod: boolean | undefined;
};

/**
 * Per-call shape accepted by `PaymentMethodTrackService.trackWorkflow`. The envelope fields are
 * omitted — they're merged in from the service's signals at dispatch.
 */
export type PaymentMethodStepMetadataInput<E extends PaymentMethodWorkflowEvent> =
  Omit<PaymentMethodWorkflowEventMetaDataMap[E], keyof PaymentMethodEventBaseMetadata>;

export type PaymentMethodWorkflowEventMetaDataMap = {
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_STARTED]: PaymentMethodEventBaseMetadata;
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_TYPE_SELECTED]: PaymentMethodEventBaseMetadata & { paymentMethodType: string };
  // `paymentMethodType` is required: the submit button is template-gated on the user having
  // picked a type, so we can stamp it on every VALIDATION_SUBMITTED. The two checkbox booleans
  // are the user's intent *as they clicked submit* — Pendo dashboards use them to compute the
  // attach-vs-save funnel without joining ADD_COMPLETE.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_VALIDATION_SUBMITTED]: PaymentMethodEventBaseMetadata & {
    paymentMethodType: string;
    shouldSave: boolean;
    shouldSetAsDefault: boolean;
  };
  // `isConnectedAccount` is true when an ACH/US bank account was linked via Stripe Financial
  // Connections (connected account / instant verification) rather than typed in manually. Omitted
  // for card / ACSS / BACS methods, where bank-link method doesn't apply.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_SETUP_CONFIRMED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; isConnectedAccount?: boolean };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_SUCCEEDED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & {
    operationDurationMs: number;
    // Stripe-sourced fields. Best-effort: undefined if the thrown error doesn't expose them.
    stripeDeclineCode?: string;
    stripeErrorCode?: string;
    // Aiwyn-side validation outcomes (duplicate card, restricted state, expired card, etc.) arrive as
    // `errorCategory: 'aiwyn-validation'`. The matching `PaymentMethodValidationError` key (DUPLICATE,
    // FUNDING, BRAND, EXPIRATION, COUNTRY, STATE, INCOMPLETE_BILLING_DETAILS) is stamped on
    // `errorSubcategory` via the FailureContext.
    // No paymentMethodId — the attach stage fires before the platform returns one.
  };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_PLATFORM_INIT_FAILED]: PaymentMethodEventBaseMetadata & FailureContext;
  // `paymentMethodType` is optional because the user can dismiss the dialog before picking a
  // type — in that case the field is omitted rather than coerced to an empty string.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_CANCELLED]: PaymentMethodEventBaseMetadata & { paymentMethodType?: string };
  // `isTemporary` mirrors the dialog's close-result flag: `true` when the new method wasn't
  // saved (one-shot use), `false` when the user opted to save it. `paymentMethodType` is the type
  // of the method that was just created/selected — required (unlike ADD_CANCELLED) since the type
  // is always known by the time the add flow completes (including the duplicate-recovery path, which
  // resolves to an already-saved method whose type is known).
  // `reusedExisting` is set (`true`) only on the duplicate-recovery path (T3-3893), where the dialog
  // resolves to an already-saved method instead of attaching a new one — so there is NO preceding
  // ATTACH_SUCCEEDED for this completion. It is omitted on the normal add path. Dashboards that join
  // ADD_COMPLETE → ATTACH_SUCCEEDED on `paymentMethodId` should exclude `reusedExisting === true`.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_COMPLETE]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; isTemporary: boolean; reusedExisting?: boolean };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_STARTED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_CANCELLED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  // `changedFields` is a comma-wrapped, comma-delimited string of the top-level `BillingDetails`
  // keys whose values differed between the dialog-open snapshot and the saved value. Format:
  // `,name,address,` for two changes, `,name,` for one, `''` (empty string) for none — same
  // empty-array-to-empty-string convention as the auth-tracking client-id formatter. The
  // leading and trailing commas on the non-empty form let Pendo dashboards filter
  // "changedFields contains ',name,'" without substring false matches. Address sub-fields
  // (line1/city/state/postalCode/country) collapse into a single `address` bucket — dashboards
  // today only need "did the address change," not which line of it. Order is fixed (name,
  // email, phone, address) for filter consistency.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_SUCCEEDED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; changedFields: string; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & { paymentMethodId: string; paymentMethodType: string; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & { paymentMethodId: string; paymentMethodType: string; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_STARTED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUBMITTED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUCCEEDED]: PaymentMethodEventBaseMetadata & {
    paymentMethodId: string;
    paymentMethodType: string;
    operationDurationMs: number;
  };
  // When the failure is the blocked case: `errorCategory: 'VALIDATION_BLOCKED'` with the `isUsedBy*`
  // booleans populated. When it's an HTTP / transport failure: whatever extractFailureContext produces.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & {
    paymentMethodId: string;
    paymentMethodType: string;
    operationDurationMs: number;
    isUsedByAutomaticPayments: boolean;
    isUsedByEngagements: boolean;
  };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUBMITTED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUCCEEDED]: PaymentMethodEventBaseMetadata & {
    paymentMethodId: string;
    paymentMethodType: string;
    operationDurationMs: number;
  };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & {
    paymentMethodId: string;
    paymentMethodType: string;
    operationDurationMs: number;
    isUsedByPendingPayments: boolean;
  };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_CANCELLED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string };
  // `wasDefault` reflects pre-delete state: `true` when the removed method was the user's
  // default at the moment delete started. Snapshotted before the platform call so the value
  // is unaffected by any default-id reshuffle the platform performs after deletion.
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_SUCCEEDED]: PaymentMethodEventBaseMetadata & { paymentMethodId: string; paymentMethodType: string; wasDefault: boolean; operationDurationMs: number };
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_FAILED]: PaymentMethodEventBaseMetadata & FailureContext & { paymentMethodId: string; paymentMethodType: string; operationDurationMs: number };
};

export const PaymentMethodEventEventTypeMap: WorkflowEventEventTypeMap<PaymentMethodWorkflowEvent> = {
  ...generateEventTypeMapDefaults(PaymentMethodWorkflowEvent, EventType.USER),
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ADD_COMPLETE]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUBMITTED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUBMITTED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_SUCCEEDED]: EventType.SYSTEM,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_PLATFORM_INIT_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_ATTACH_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_UPDATE_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_SET_DEFAULT_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_DOMAIN_VALIDATION_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_PLATFORM_VALIDATION_FAILED]: EventType.ERROR,
  [PaymentMethodWorkflowEvent.PAYMENT_METHOD_REMOVE_FAILED]: EventType.ERROR,
};

/**
 * `errorCategory` value emitted on REMOVE_*_VALIDATION_FAILED when the validation outcome was
 * "blocked" (active usage prevents removal). Distinct from the HTTP-derived ErrorCategory values
 * — dashboards filtering on `errorCategory === 'VALIDATION_BLOCKED'` get the blocked-by-usage
 * count without needing to inspect `isUsedBy*` flags directly.
 */
export const VALIDATION_BLOCKED_ERROR_CATEGORY = 'VALIDATION_BLOCKED' as const;

/**
 * `errorCategory` value emitted on PAYMENT_METHOD_ATTACH_FAILED when the failure is an Aiwyn-side
 * validation outcome rather than a Stripe / HTTP error. The matching `PaymentMethodValidationError`
 * key is stamped on `errorSubcategory`.
 */
export const AIWYN_VALIDATION_ERROR_CATEGORY = 'aiwyn-validation' as const;

/**
 * Precedence order used to pick a single `errorSubcategory` value when multiple Aiwyn-side
 * validation rules failed simultaneously. DUPLICATE first because re-add-of-existing-card is the
 * dashboard-relevant case. Mirrors `PaymentPlatformService.getValidationErrorMessage` so the
 * discriminator stamped on the FAILED event matches the rule that produced the user-facing message.
 */
export const PAYMENT_METHOD_VALIDATION_ERROR_PRECEDENCE: readonly PaymentMethodValidationError[] = [
  PaymentMethodValidationError.DUPLICATE,
  PaymentMethodValidationError.FUNDING,
  PaymentMethodValidationError.BRAND,
  PaymentMethodValidationError.EXPIRATION,
  PaymentMethodValidationError.COUNTRY,
  PaymentMethodValidationError.STATE,
  PaymentMethodValidationError.INCOMPLETE_BILLING_DETAILS,
];

/**
 * Runtime guard for "is this a payment-method workflow event."
 */
export function isPaymentMethodWorkflowEvent(step: string): step is PaymentMethodWorkflowEvent {
  return Object.prototype.hasOwnProperty.call(PaymentMethodEventEventTypeMap, step);
}
